import { Buttons, CancelButton, Checkbox, DateTime, DeleteButton, HelpTooltip, Level, ModalCard } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import { accountingProviders } from '~/lookups';
import React, { useState } from 'react';
import styled from 'styled-components';

const P = styled.p`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

function DeleteDialog({ creditNote, onDelete, onClose }) {
  const { workspace } = useWorkspace();
  const [removeFromAccounting, setRemoveFromAccounting] = useState(creditNote.defaultRemoveFromAccounting ?? false);
  const [{ isSubmitting }, form] = useForm();
  const toast = useToast();
  const api = useApi();

  const accountingProviderName =
    creditNote.accountingProvider && accountingProviders[creditNote.accountingProvider].name;

  const handleSubmit = async () => {
    form.submit();

    if (removeFromAccounting && accountingProviderName && creditNote.accountingSynced) {
      try {
        await api.www
          .workspaces(workspace.id)
          .creditNotes(creditNote.id)
          .removeFromAccountingIntegration({ accountingProvider: creditNote.accountingProvider });
      } catch (error) {
        toast.error(
          error.message ??
            `An error has occurred. The credit note has not been removed from ${accountingProviderName}.`,
        );
      }
    }

    await api.www.workspaces(workspace.id).creditNotes(creditNote.id).delete();

    if (onDelete) await onDelete();
    form.done();
    toast.success(`Credit Note #${creditNote.number} has been deleted.`);
  };

  const title = creditNote.status.id === 'draft' ? 'Delete Draft Credit Note' : 'Delete Published Credit Note';

  return (
    <ModalCard title={title} onClose={onClose}>
      <ModalCard.Body>
        <P>
          Are you sure you want to delete {creditNote.status.id === 'draft' ? 'draft' : 'published'} credit note #
          {creditNote.number} for the <strong>{creditNote.client.name}</strong> client?
        </P>
        {creditNote.sentAt && (
          <P>
            This credit note was sent to the client on <DateTime value={creditNote.sentAt} />.
          </P>
        )}
      </ModalCard.Body>

      <ModalCard.Footer>
        <Level>
          {creditNote.accountingSynced && accountingProviderName && (
            <Level.Item>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  label={`Remove from ${accountingProviderName}`}
                  subText={creditNote.accountingConnectionName}
                  checked={removeFromAccounting}
                  onChange={() => setRemoveFromAccounting(!removeFromAccounting)}
                />
                {creditNote.accountingProvider === 'qbo' && (
                  <HelpTooltip
                    message="Any payments associated with this credit note will be removed from QuickBooks."
                    style={{ marginLeft: '0.5rem' }}
                  />
                )}
              </div>
            </Level.Item>
          )}

          <Level.Item>
            <Buttons align="right">
              <CancelButton onClick={onClose}>Cancel</CancelButton>
              <DeleteButton isLoading={isSubmitting} onClick={handleSubmit}>
                Delete
              </DeleteButton>
            </Buttons>
          </Level.Item>
        </Level>
      </ModalCard.Footer>
    </ModalCard>
  );
}

export default DeleteDialog;
